/* eslint-disable camelcase */
/* eslint-disable max-lines-per-function */
import { useKeycloak } from "@react-keycloak/web";
import React, { ReactElement, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { KeycloakProfile } from "keycloak-js";
import APIService from "../../services/API";
import { AuthService } from "../../services/auth";
import { AppStorage } from "../../services/Storage";
import { AuthActions, AuthProps, AuthState } from "./AuthInterface";
import { LoginDtoType, LoginDtoResponseType } from "@mindmom/common";

export interface User {
    username: null | string;
    description: null | string;
    avatar_url: string | null;
}

const initialState: AuthState = {
    isAuth: false,
    authErrors: {},
    authMessages: {},
};

interface Props {
    // eslint-disable-next-line no-undef
    children: JSX.Element | JSX.Element[];
}
export const AuthContext = React.createContext<AuthProps>({
    state: initialState,
    coach: null,
    InsuranceExportPermission: false,
    CreateTesterPermission: false,
    GenerateVouchersPermission: false,
    // eslint-disable-next-line no-inline-comments, no-warning-comments, line-comment-position
    AssignPriorityPermission: false,
    DeleteUsersPermission: false,
    actions: {} as AuthActions,
});

function AuthContexProvider({ children }: Props): ReactElement {
    const { keycloak } = useKeycloak();
    const [InsuranceExportPermission, setInsuranceExportPermission] =
        useState(false);
    const [CreateTesterPermission, setCreateTesterPermission] = useState(false);
    const [GenerateVouchersPermission, setGenerateVouchersPermission] =
        useState(false);

    const [AssignPriorityPermission, setAssignPriorityPermission] =
        // eslint-disable-next-line no-inline-comments, no-warning-comments, line-comment-position
        useState(false);
    const [DeleteUsersPermission, setDeleteUsersPermission] = useState(false);
    const history = useHistory();
    const [coach, setCoach] = useState<null | User>(null);
    // helpers
    const getHydratedState = (): AuthState | false => {
        const hydrate = AppStorage.getItem("authState");
        if (!hydrate) {
            return false;
        }
        return JSON.parse(hydrate) as AuthState;
    };

    const checkIfUserIsInsuranceExportPermission = () => {
        keycloak.loadUserProfile().then((userProfile: KeycloakProfile) => {
            if (
                userProfile.attributes?.InsuranceExportPermission &&
                (userProfile.attributes?.InsuranceExportPermission as string[])[0] === "true"
            ) {
                setInsuranceExportPermission(true);
            }
        });
    };

    const checkIfUserHasCreateTesterPermission = () => {
        keycloak.loadUserProfile().then((userProfile: KeycloakProfile) => {
            if (
                userProfile.attributes?.CreateTesterPermission &&
                (userProfile.attributes?.CreateTesterPermission as string[])[0] === "true"
            ) {
                setCreateTesterPermission(true);
            }
        });
    };
    const checkIfUserHasGenerateVouchersPermission = () => {
        keycloak.loadUserProfile().then((userProfile: KeycloakProfile) => {
            if (
                userProfile.attributes?.GenerateVouchersPermission &&
                (userProfile.attributes?.GenerateVouchersPermission as string[])[0] === "true"
            ) {
                setGenerateVouchersPermission(true);
            }
        });
    };

    const checkIfUserHasAssignPriorityPermission = () => {
        keycloak.loadUserProfile().then((userProfile: KeycloakProfile) => {
            if (
                userProfile.attributes?.AssignPriorityPermission &&
                (userProfile.attributes?.AssignPriorityPermission as string[])[0] === "true"
            ) {
                setAssignPriorityPermission(true);
            }
        });
    };

    const checkIfUserHasDeleteUsersPermission = () => {
        keycloak.loadUserProfile().then((userProfile: KeycloakProfile) => {
            if (
                userProfile.attributes?.DeleteUsersPermission &&
                (userProfile.attributes?.DeleteUsersPermission as string[])[0] === "true"
            ) {
                setDeleteUsersPermission(true);
            }
        });
    };

    useEffect(() => {
        if (keycloak.authenticated) {
            checkIfUserIsInsuranceExportPermission();
            checkIfUserHasCreateTesterPermission();
            checkIfUserHasGenerateVouchersPermission();
            checkIfUserHasAssignPriorityPermission();
            checkIfUserHasDeleteUsersPermission();
        }
    }, [keycloak.authenticated]);

    const [authState, setAuthState] = React.useState<AuthState>(
        getHydratedState() !== false
            ? (getHydratedState() as AuthState)
            : initialState
    );
    // actions
    const authenticateUser = async (
        email: string,
        password: string
    ): Promise<void> => {
        let data;
        try {
            data = await APIService.handlePublicPostRequest<
                LoginDtoType,
                LoginDtoResponseType
            >(
                {
                    email,
                    password,
                },
                "/auth/login"
            );
        } catch (error) {
            alert("wrong email or password");
            return;
        }
        AuthService.saveAuthDataToLocalStorage(data);
        setAuthState({
            ...authState,
            isAuth: true,
        });
        AppStorage.saveItem(
            "authState",
            JSON.stringify({
                ...authState,
                isAuth: true,
            })
        );
        history.push("/");
    };

    const resetPassword = async (email: string): Promise<void> => { };

    const logout = async (): Promise<void> => {
        await keycloak.logout();
        history.push("/");
    };

    // render
    return (
        <AuthContext.Provider
            value={{
                InsuranceExportPermission,
                CreateTesterPermission,
                GenerateVouchersPermission,
                AssignPriorityPermission,
                DeleteUsersPermission,
                state: authState,
                coach,
                actions: {
                    setCoach,
                    authenticateUser,
                    logout,
                    resetPassword,
                },
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
export { AuthContexProvider };
